import { CircleHelp } from 'lucide-react';

import type { CandidateDetailData } from '@/modules/candidate-detail/types';
import {
  CandidateLedgerAssessment,
  LedgerRevisionComparisonNotice,
} from '@/modules/candidates/components/CandidateLedgerAssessment';

import { CandidateFindingList } from './CandidateFindingList';
import { CandidateSectionTitle } from './CandidateSectionTitle';

interface CandidateSummaryProps {
  candidate: CandidateDetailData;
}

export function CandidateSummary({ candidate }: CandidateSummaryProps) {
  const { assessment } = candidate;

  if (!assessment) {
    return (
      <p className="text-muted-foreground border-y py-4 text-[13px]">
        No assessment yet for {candidate.firstName}. Experience and activity may
        still provide useful context.
      </p>
    );
  }

  if (assessment.mode === 'ledger') {
    if (!assessment.ledger) {
      return (
        <p className="text-muted-foreground border-y py-4 text-[13px]">
          The candidate assessment is not available yet.
        </p>
      );
    }

    const showSubmissionRevision =
      assessment.atSubmissionLedger &&
      assessment.atSubmissionLedger.revisionId !== assessment.ledger.revisionId;

    return (
      <div className="flex flex-col gap-6">
        <CandidateLedgerAssessment
          assessment={assessment.ledger}
          title="Current candidate assessment"
          description="Evidence evaluated against the job's current requirements."
          showMetrics={false}
        />
        {showSubmissionRevision && assessment.atSubmissionLedger && (
          <>
            <LedgerRevisionComparisonNotice
              current={assessment.ledger}
              atSubmission={assessment.atSubmissionLedger}
              showScore={false}
            />
            <CandidateLedgerAssessment
              assessment={assessment.atSubmissionLedger}
              title="Assessment at submission"
              description="The assessment Finlay used when this candidate entered your review queue."
              compact
              showMetrics={false}
            />
          </>
        )}
      </div>
    );
  }

  const questions = [...assessment.openQuestions, ...assessment.concerns];

  return (
    <div className="flex flex-col gap-8">
      <section
        aria-labelledby="fit-summary-heading"
        className="flex flex-col gap-3"
      >
        <CandidateSectionTitle id="fit-summary-heading">
          Fit summary
        </CandidateSectionTitle>
        {assessment.summary ? (
          <p className="max-w-[64ch] text-[15px] leading-6">
            {assessment.summary}
          </p>
        ) : (
          <p className="text-muted-foreground text-[13px]">
            No fit summary was captured.
          </p>
        )}
      </section>

      <section
        aria-labelledby="strengths-heading"
        className="flex flex-col gap-3"
      >
        <CandidateSectionTitle
          hint="The strongest evidence for this role."
          id="strengths-heading"
        >
          Why {candidate.firstName}
        </CandidateSectionTitle>
        <CandidateFindingList
          emptyText="No distinct strengths were captured in the assessment."
          findings={assessment.strengths}
        />
      </section>

      {questions.length > 0 && (
        <section
          aria-labelledby="questions-heading"
          className="flex flex-col gap-3"
        >
          <CandidateSectionTitle
            hint="Worth raising in the first interview."
            id="questions-heading"
          >
            Questions and concerns
          </CandidateSectionTitle>
          <CandidateFindingList
            emptyText="No open questions were recorded."
            findings={questions}
            icon={CircleHelp}
            iconClassName="text-neutral-500"
          />
        </section>
      )}
    </div>
  );
}
